// Phase 85.A.83 — Upload Consent Wizard (Step 1)
//
// Wizard bước 1 BẮT BUỘC user xem trước khi mở file picker upload Avatar
// hoặc OCR Rating screenshot. Mục đích: giảm tỉ lệ user upload sai loại
// ảnh → giảm tải duyệt thủ công của Admin.
//
// User choices (handoff Phase 85.A.83):
//   • Type: "avatar" | "rating"
//   • Slider "Tôi đồng ý" lock 10s (countdown), sau đó user phải kéo handle
//     từ trái sang phải để confirm. Hiện modal MỖI LẦN user mở upload
//     (không skip qua localStorage).
//
// Usage:
//   <UploadConsentWizard
//     open={showConsent}
//     type="avatar"
//     onAgree={() => { setShowConsent(false); openFilePicker(); }}
//     onClose={() => setShowConsent(false)}
//   />
import { useEffect, useRef, useState, useCallback } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  X,
  CheckCircle,
  Warning,
  CaretDoubleRight,
  Lock,
  MagnifyingGlassPlus,
} from "@phosphor-icons/react";

const COUNTDOWN_SECONDS = 10;

const VARIANTS = {
  avatar: {
    title: "Hướng dẫn chọn ảnh đại diện",
    subtitle: "Vui lòng đọc kỹ trước khi upload",
    imageSrc: "/guides/avatar-guide.png",
    imageAlt: "Hướng dẫn chọn ảnh avatar — 3 ví dụ SAI và 1 ví dụ ĐÚNG",
    checklist: [
      "Ảnh chân dung CHÍNH BẠN — nhìn rõ mặt, đủ sáng.",
      "Không phải ảnh người thân, con cái, vợ/chồng hay VĐV khác.",
      "Không phải ảnh động vật, thiên nhiên, logo, hay ảnh do AI tạo.",
      "Ảnh không hợp lệ sẽ bị BTC từ chối, có thể ảnh hưởng xét duyệt giải.",
    ],
    // v16.60.15 (Feb 2026) — Apple 5.1.1(i) & 5.1.2(i): disclosure về AI
    // service phân tích ảnh avatar (OCR + logo detect chống mạo danh).
    aiDisclosure: {
      title: "AI phân tích ảnh — cần đồng ý",
      lines: [
        "PickLeague gửi ảnh avatar bạn upload tới dịch vụ AI (Google Gemini hoặc OpenAI GPT-4o qua Emergent LLM Gateway) để phát hiện logo, watermark, chữ trong ảnh — chống mạo danh.",
        "KHÔNG gửi: tên, SĐT, email, hay dữ liệu cá nhân nào khác.",
        "Bạn có thể yêu cầu xoá dữ liệu AI đã xử lý qua support@pickleague.vn.",
      ],
    },
    consentText:
      "Tôi đã đọc & hiểu hướng dẫn, cam kết upload ảnh chân dung của chính tôi, và ĐỒNG Ý ảnh được xử lý bởi AI để chống mạo danh.",
  },
  rating: {
    title: "Hướng dẫn upload ảnh điểm trình",
    subtitle: "Vui lòng đọc kỹ trước khi nộp screenshot",
    imageSrc: "/guides/rating-ocr-guide.png",
    imageAlt: "Hướng dẫn upload OCR điểm trình — 3 ví dụ SAI và 1 ví dụ ĐÚNG",
    checklist: [
      "Là SCREENSHOT từ app/website khác (DUPR, BaseLine…) — KHÔNG phải avatar.",
      "Ảnh phải thấy rõ Họ tên · Nickname · điểm trình hiện tại.",
      "KHÔNG được chụp màn hình từ chính PickLeague — phải là nền tảng khác.",
      "Ảnh không đủ thông tin hoặc sai nền tảng sẽ bị từ chối ngay.",
    ],
    consentText:
      "Tôi cam kết đây là ảnh screenshot điểm trình của tôi trên nền tảng KHÁC.",
  },
};

/**
 * Slider "Tôi đồng ý" với countdown 10s.
 *
 * Props:
 *   - disabled: countdown chưa kết thúc → true
 *   - onConfirm: callback khi user kéo đến ≥ 90% track width
 *   - countdownLeft: số giây còn lại của countdown
 */
function ConsentSlider({ disabled, countdownLeft, onConfirm }) {
  const trackRef = useRef(null);
  const [x, setX] = useState(0); // px offset của handle từ trái track
  const [dragging, setDragging] = useState(false);
  const [done, setDone] = useState(false);
  const startXRef = useRef(0);
  const startOffsetRef = useRef(0);

  // Reset position khi disabled chuyển true (vd modal re-open)
  useEffect(() => {
    if (disabled) {
      setX(0);
      setDone(false);
    }
  }, [disabled]);

  const getTrackMax = useCallback(() => {
    const t = trackRef.current;
    if (!t) return 0;
    // Handle width = 56px; max travel = trackWidth - handleWidth - padding
    return Math.max(0, t.clientWidth - 56 - 8);
  }, []);

  const onPointerDown = (e) => {
    if (disabled || done) return;
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    startXRef.current = point.clientX;
    startOffsetRef.current = x;
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - startXRef.current;
      const max = getTrackMax();
      const next = Math.min(Math.max(startOffsetRef.current + dx, 0), max);
      setX(next);
    };
    const handleUp = () => {
      setDragging(false);
      const max = getTrackMax();
      // Threshold 88% → fire confirm + lock slider ở vị trí end
      if (max > 0 && x >= max * 0.88) {
        setX(max);
        setDone(true);
        // Phase 85.A.85 — iOS Safari chặn file picker nếu nằm ngoài
        // user-gesture chain. Phải gọi onConfirm SYNCHRONOUSLY ngay
        // trong pointerup để caller có thể trigger input.click() trong
        // cùng tick gesture. KHÔNG dùng setTimeout!
        onConfirm?.();
      } else {
        // Snap back animated
        setX(0);
      }
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [dragging, x, getTrackMax, onConfirm]);

  const trackMax = getTrackMax();
  const pct = trackMax > 0 ? Math.round((x / trackMax) * 100) : 0;
  const labelText = done
    ? "Đã đồng ý ✓"
    : disabled
      ? `Chờ ${countdownLeft}s để mở khoá…`
      : "Kéo sang phải để đồng ý →";

  return (
    <div
      ref={trackRef}
      className={`relative h-14 rounded-full border-2 select-none transition-colors ${
        disabled
          ? "border-white/15 bg-white/[0.04]"
          : done
            ? "border-emerald-400/70 bg-emerald-500/20"
            : "border-[#FF8A00]/60 bg-gradient-to-r from-[#FF8A00]/15 to-[#FF9F1C]/20"
      }`}
      data-testid="consent-slider-track"
    >
      {/* Progress fill behind handle */}
      <div
        className={`absolute top-0 left-0 h-full rounded-full transition-colors ${
          done
            ? "bg-emerald-500/40"
            : disabled
              ? "bg-white/[0.03]"
              : "bg-gradient-to-r from-[#FF8A00]/40 to-[#FF9F1C]/50"
        }`}
        style={{
          width: `${Math.max(64, x + 56)}px`,
          transition: dragging ? "none" : "width 0.25s ease-out",
        }}
      />

      {/* Label center */}
      <div
        className={`absolute inset-0 flex items-center justify-center pointer-events-none text-[12px] font-black tracking-wide ${
          done
            ? "text-emerald-200"
            : disabled
              ? "text-white/45"
              : "text-white"
        }`}
        style={{ paddingLeft: "64px" }}
      >
        {disabled ? (
          <span className="inline-flex items-center gap-1.5">
            <Lock size={13} weight="bold" />
            {labelText}
          </span>
        ) : done ? (
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={14} weight="fill" />
            {labelText}
          </span>
        ) : (
          <span>{labelText}</span>
        )}
      </div>

      {/* Handle */}
      <button
        type="button"
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        disabled={disabled || done}
        className={`absolute top-1 left-1 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-colors ${
          disabled
            ? "bg-white/10 cursor-not-allowed"
            : done
              ? "bg-emerald-500 cursor-default"
              : "bg-gradient-to-br from-[#FF8A00] to-[#FF9F1C] cursor-grab active:cursor-grabbing"
        }`}
        style={{
          transform: `translateX(${x}px)`,
          transition: dragging ? "none" : "transform 0.25s ease-out",
          touchAction: "none",
        }}
        aria-label="Kéo sang phải để đồng ý"
        data-testid="consent-slider-handle"
        data-progress={pct}
      >
        {disabled ? (
          <Lock size={18} weight="bold" className="text-white/45" />
        ) : done ? (
          <CheckCircle size={20} weight="fill" className="text-white" />
        ) : (
          <CaretDoubleRight size={18} weight="bold" className="text-white" />
        )}
      </button>
    </div>
  );
}

export default function UploadConsentWizard({
  open,
  type = "avatar", // "avatar" | "rating"
  onAgree,
  onClose,
}) {
  const variant = VARIANTS[type] || VARIANTS.avatar;
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [zoomed, setZoomed] = useState(false);

  // Reset countdown mỗi lần mở modal
  useEffect(() => {
    if (!open) return;
    setCountdown(COUNTDOWN_SECONDS);
    setZoomed(false);
    const t = setInterval(() => {
      setCountdown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [open]);

  const disabled = countdown > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pl-fixed-fullscreen z-[400] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md px-3 pb-3 sm:pb-0"
          data-testid="upload-consent-wizard"
          data-variant={type}
          onClick={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ y: 40, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 40, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className="w-full max-w-md rounded-3xl border border-[#FF8A00]/35 bg-gradient-to-br from-[#1A1208] via-[#0F1623] to-[#0A0F1C] shadow-[0_24px_80px_rgba(255,138,0,0.4)] overflow-hidden max-h-[95dvh] flex flex-col"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-start gap-3 border-b border-white/8 bg-gradient-to-r from-[#FF8A00]/12 to-transparent">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FF8A00]/35 to-[#FF9F1C]/15 border border-[#FF8A00]/45 flex items-center justify-center shrink-0">
                <Warning size={20} weight="fill" className="text-[#FFB300]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#FFB300]/85">
                  Bước 1 · Đọc hướng dẫn
                </div>
                <div className="text-[16px] font-black text-white leading-tight mt-0.5" data-testid="consent-wizard-title">
                  {variant.title}
                </div>
                <div className="text-[11px] text-white/55 mt-0.5">
                  {variant.subtitle}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-xl hover:bg-white/8 flex items-center justify-center text-white/55 hover:text-white transition shrink-0"
                aria-label="Đóng"
                data-testid="consent-wizard-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="px-5 py-4 space-y-4 overflow-y-auto">
              {/* Guide image (click to zoom) */}
              <button
                type="button"
                onClick={() => setZoomed(true)}
                className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-black/40 group"
                data-testid="consent-wizard-guide-image-btn"
              >
                <img
                  src={variant.imageSrc}
                  alt={variant.imageAlt}
                  loading="lazy"
                  className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform"
                />
                <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-80 group-hover:opacity-100">
                  <MagnifyingGlassPlus size={14} weight="bold" className="text-white" />
                </div>
              </button>

              {/* Checklist */}
              <ul className="space-y-2" data-testid="consent-wizard-checklist">
                {variant.checklist.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-[12.5px] text-white/85 leading-relaxed"
                  >
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className="text-emerald-400 shrink-0 mt-0.5"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* v16.60.15 (Feb 2026) — AI disclosure box (Apple 5.1.1(i) & 5.1.2(i)):
                  Chỉ hiện khi variant có aiDisclosure (variant "avatar"). */}
              {variant.aiDisclosure && (
                <div
                  className="rounded-xl border border-sky-400/35 bg-sky-500/[0.08] p-3 space-y-1.5"
                  data-testid="consent-wizard-ai-disclosure"
                >
                  <div className="text-[11.5px] font-black text-sky-200 uppercase tracking-wider">
                    🤖 {variant.aiDisclosure.title}
                  </div>
                  <ul className="text-[11.5px] text-sky-100/90 leading-snug space-y-1 list-disc ml-4">
                    {variant.aiDisclosure.lines.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Consent text */}
              <div className="rounded-xl border border-amber-400/30 bg-amber-500/[0.06] p-3 text-[12px] text-amber-100/90 leading-snug font-medium" data-testid="consent-wizard-text">
                {variant.consentText}
              </div>
            </div>

            {/* Footer with slider */}
            <div className="px-5 py-4 border-t border-white/8 bg-black/30 space-y-2">
              <ConsentSlider
                disabled={disabled}
                countdownLeft={countdown}
                onConfirm={onAgree}
              />
              <p className="text-[10.5px] text-white/45 text-center font-medium">
                {disabled
                  ? `Bạn cần đọc hướng dẫn trong ${countdown} giây trước khi mở khoá nút đồng ý.`
                  : "Kéo nút cam sang phải để tiếp tục upload."}
              </p>
            </div>
          </motion.div>

          {/* Zoom lightbox */}
          {zoomed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[410] bg-black/95 flex items-center justify-center p-4"
              onClick={() => setZoomed(false)}
              data-testid="consent-wizard-zoom-overlay"
            >
              <img
                src={variant.imageSrc}
                alt={variant.imageAlt}
                className="max-w-full max-h-full object-contain"
              />
              <button
                type="button"
                onClick={() => setZoomed(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Đóng zoom"
              >
                <X size={18} weight="bold" className="text-white" />
              </button>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
